import { nanoid } from "nanoid";
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import type { ChatMessage } from "~/chat/types/chat";
import { sendChatPrompt } from "~/chat/api/send-chat-prompt";
import { getConversationQueryOptions } from "~/conversations/queries/get-conversation-query";
import { getConversationSourcesQueryOptions } from "~/conversations/queries/get-conversation-sources-query";

type PromptMessageOptions = {
  signal: AbortSignal;
};

type PromptMessagePayload = {
  message: string;
  conversationId: number;
  workspaceId: number;
  files?: File[] | null;
};

function usePromptMessageMutation({ signal }: PromptMessageOptions) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    retry: false,
    mutationKey: ["workspace", "conversation", "prompt"],
    async mutationFn(payload: PromptMessagePayload) {
      const userMessageId = nanoid();
      const assistantPromptId = nanoid();

      const prompt = sendChatPrompt(
        {
          role: "user",
          id: userMessageId,
          content: payload.message,
        },
        {
          signal,
          workspaceId: payload.workspaceId,
          conversationId: payload.conversationId,
          files: payload.files,
        },
      );

      for await (const intermediaryCompletionMessage of prompt) {
        if (intermediaryCompletionMessage.error) {
          throw new Error(intermediaryCompletionMessage.error);
        }
        const intermediaryCompletion: ChatMessage = {
          ...intermediaryCompletionMessage,
          role: "assistant",
          id: assistantPromptId,
        };

        queryClient.setQueryData(
          getConversationQueryOptions({
            conversationId: payload.conversationId,
          }).queryKey,
          (data) => {
            if (!data) {
              return data;
            }

            return {
              ...data,
              updatedAt: new Date(),
              messages: data.messages
                ?.filter((message) => message.id !== intermediaryCompletion.id)
                .concat(intermediaryCompletion),
            };
          },
        );
      }
    },
    onMutate(payload) {
      if (typeof payload.conversationId === "undefined") {
        return;
      }

      queryClient.setQueryData(
        getConversationQueryOptions({
          conversationId: payload.conversationId,
        }).queryKey,
        (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            updatedAt: new Date(),
            messages: data.messages?.concat({
              id: nanoid(),
              role: "user",
              content: payload.message,
              attachments: payload.files
                ? payload.files.map((file) => ({ filename: file.name }))
                : [],
            }),
          };
        },
      );
    },
    onSuccess(_, payload) {
      queryClient.invalidateQueries({
        queryKey: getConversationSourcesQueryOptions({
          conversationId: payload.conversationId,
        }).queryKey,
        exact: true,
      });
    },
  });

  return mutation;
}

function useIsPromptMutating({
  conversationId,
  workspaceId,
}: Partial<Pick<PromptMessagePayload, "conversationId" | "workspaceId">>) {
  const mutationCount = useIsMutating({
    status: "pending",
    mutationKey: ["workspace", "conversation", "prompt"],
    predicate: (mutation) => {
      if (
        !mutation.state.variables ||
        typeof mutation.state.variables !== "object"
      ) {
        return false;
      }

      if (
        "workspaceId" in mutation.state.variables &&
        "conversationId" in mutation.state.variables
      ) {
        return (
          mutation.state.variables.workspaceId === workspaceId &&
          mutation.state.variables.conversationId === conversationId
        );
      }

      return false;
    },
  });

  return mutationCount > 0;
}

function useIsAnyPromptMutating({
  workspaceId,
}: Partial<Pick<PromptMessagePayload, "workspaceId">>) {
  const mutationCount = useIsMutating({
    status: "pending",
    mutationKey: ["workspace", "conversation", "prompt"],
    predicate: (mutation) => {
      if (
        !mutation.state.variables ||
        typeof mutation.state.variables !== "object"
      ) {
        return false;
      }

      if ("workspaceId" in mutation.state.variables) {
        return mutation.state.variables.workspaceId === workspaceId;
      }

      return false;
    },
  });

  return mutationCount > 0;
}

export {
  usePromptMessageMutation,
  useIsPromptMutating,
  useIsAnyPromptMutating,
};
